import { useEffect, useState } from 'react';
import { supabase } from '../../../supabase';
import { v4 as uuidv4 } from 'uuid';
import Button from '../../../components/common/Button';
import Textarea from '../../../components/common/Textarea';
import styles from './LevelGuide.module.css';
import Toast from '../../../components/common/Toast';

export default function LevelGuide() {
  const [levels, setLevels] = useState([]);
  const [browserId, setBrowserId] = useState('');
  const [userFeedbacks, setUserFeedbacks] = useState({});
  const [showLevelModal, setShowLevelModal] = useState(false);
  const [managerLevel, setManagerLevel] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  useEffect(() => {
    fetchLevels();
    initializeBrowserId();
    checkManagerLevel();
  }, []);

  useEffect(() => {
    document.body.style.backgroundColor = 'var(--gray-50)';

    return () => {
      document.body.style.backgroundColor = '';
    };
  }, []);

  useEffect(() => {
    if (showLevelModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showLevelModal]);

  const initializeBrowserId = () => {
    let id = localStorage.getItem('browserId');
    if (!id) {
      id = uuidv4();
      localStorage.setItem('browserId', id);
    }
    setBrowserId(id);
    fetchUserFeedbacks(id);
  };

  const fetchLevels = async () => {
    try {
      const { data, error } = await supabase
        .from('level_group')
        .select('*')
        .order('id');
      
      if (error) throw error;
      setLevels(data);
    } catch (error) {
      console.error('레벨 데이터를 불러오는 중 에러가 발생했습니다:', error);
    }
  };

  const fetchUserFeedbacks = async (id) => {
    try {
      const { data, error } = await supabase
        .from('feedback_level_guide')
        .select('*')
        .eq('uuid', id);
      
      if (error) throw error;
      
      const feedbackMap = {};
      data.forEach(feedback => {
        feedbackMap[feedback.level_group_id] = feedback.feedback;
      });
      setUserFeedbacks(feedbackMap);
    } catch (error) {
      console.error('피드백을 불러오는 중 에러가 발생했습니다:', error);
    }
  };

  const checkManagerLevel = () => {
    const savedLevel = localStorage.getItem('managerLevel');
    if (savedLevel) {
      setManagerLevel(savedLevel);
    } else {
      setShowLevelModal(true);
    }
  };

  const managerLevelOptions = [
    { label: '10경기 미만', value: '비기너' },
    { label: '10~50경기', value: '아마추어' },
    { label: '50~100경기', value: '세미프로' },
    { label: '100경기 이상', value: '프로' }
  ];

  const handleManagerLevelSelect = (selectedLabel) => {
    const selectedOption = managerLevelOptions.find(option => option.label === selectedLabel);
    if (selectedOption) {
      setManagerLevel(selectedOption.value);
      localStorage.setItem('managerLevel', selectedOption.value);
      setShowLevelModal(false);
    }
  };

  const handleFeedback = async (levelId, feedback) => {
    if (!managerLevel) {
      alert('매니저 숙련도를 먼저 선택해주세요.');
      setShowLevelModal(true);
      return;
    }

    try {
      const existingFeedback = userFeedbacks[levelId];
      
      if (existingFeedback) {
        const { error } = await supabase
          .from('feedback_level_guide')
          .update({ 
            feedback,
            manager_level: managerLevel
          })
          .match({ uuid: browserId, level_group_id: levelId });
          
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('feedback_level_guide')
          .insert([
            {
              uuid: browserId,
              level_group_id: levelId,
              feedback,
              manager_level: managerLevel
            }
          ]);
          
        if (error) throw error;
      }

      setUserFeedbacks(prev => ({
        ...prev,
        [levelId]: feedback
      }));
      
      showToastMessage('저장됐어요!');
    } catch (error) {
      console.error('저장에 실패했어요:', error);
    }
  };

  const feedbackOptions = [
    '적절해요',
    '기준이 높아요',
    '기준이 낮아요',
    '설명이 부족해요',
    '이해가 어려워요'
  ];

  const getVisitorId = () => {
    let persistentId = localStorage.getItem('visitorId');
    let sessionId = sessionStorage.getItem('sessionId');
    
    if (!persistentId) {
      persistentId = uuidv4();
      localStorage.setItem('visitorId', persistentId);
    }
    
    if (!sessionId) {
      sessionId = uuidv4();
      sessionStorage.setItem('sessionId', sessionId);
    }
    
    return { persistentId, sessionId };
  };

  const formatDetail = (detail) => {
    // 각 줄을 분리
    const lines = detail.split('\n');
    
    return lines.map((line, index) => {
      const [label, ...rest] = line.split(':');
      const text = rest.join(':'); // 텍스트에 : 가 포함될 수 있으므로 나머지 부분을 다시 결합

      return (
        <p key={index} className={styles.detail}>
          <span className={styles.detailLabel}>{label}:</span>
          <span className={styles.detailText}>{text}</span>
        </p>
      );
    });
  };

  const getLevelIconUrl = (levelName, levelId) => {
    // level_group_id가 13 또는 14인 경우 null 반환
    if (levelId === 13 || levelId === 14) {
      return null;
    }

    // 레벨 이름에서 아이콘 파일명 생성
    const levelNameLower = levelName.toLowerCase();
    let iconName = '';

    if (levelNameLower.includes('비기너')) {
      iconName = 'beginner' + levelNameLower.match(/\d+/)[0];
    } else if (levelNameLower.includes('아마추어')) {
      iconName = 'amateur' + levelNameLower.match(/\d+/)[0];
    } else if (levelNameLower.includes('세미프로')) {
      iconName = 'semipro' + levelNameLower.match(/\d+/)[0];
    } else if (levelNameLower.includes('프로')) {
      iconName = 'pro';
    }

    return iconName ? `${supabase.supabaseUrl}/storage/v1/object/public/asset/level/${iconName}.png` : null;
  };

  const handleFeedbackSubmit = async () => {
    if (!feedback.trim()) {
      alert('피드백을 입력해주세요.');
      return;
    }

    if (!managerLevel) {
      alert('매니저 숙련도를 먼저 선택해주세요.');
      setShowLevelModal(true);
      return;
    }

    try {
      const { error } = await supabase
        .from('feedback_level_guide')
        .insert([
          {
            uuid: browserId,
            level_group_id: null,
            feedback: feedback.trim(),
            manager_level: managerLevel
          }
        ]);
        
      if (error) throw error;

      setFeedbackSubmitted(true);
      showToastMessage('저장됐어요');
      setFeedback('');
    } catch (error) {
      console.error('피드백 제출 중 에러가 발생했습니다:', error);
      alert('피드백 제출에 실패했습니다. 다시 시도해주세요.');
    }
  };

  const showToastMessage = (message) => {
    setToastMessage(message);
    setShowToast(true);
  };

  return (
    <div className={styles.container}>
      {showLevelModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <h2>매치 진행 수를 선택해주세요.</h2>
            <p>정확하지 않아도 괜찮아요. 대략 알고 있는 정도를 선택해주세요.</p>
            <div className={styles.modalButtons}>
              {managerLevelOptions.map((option) => (
                <Button
                  key={option.label}
                  onClick={() => handleManagerLevelSelect(option.label)}
                  width='fullWidth'
                  size='medium'
                  type='outline'
                  style='secondary'
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      )}
        <div className={styles.titleContainer}>
            <h1 className={styles.title}>플랩 레벨 가이드 1.5</h1>
            <p>매니저님! 1월 13일부터 레벨 시스템이 개편돼요. 변경된 레벨 가이드를 미리 확인하고 피드백을 남겨주세요. 😀</p>
            <div className={styles.details}>
                <h4>주요 변경 사항</h4>
                <p>- 스타터와 비기너 통합</p>
                <p>- 아마추어 5단계 세분화</p>
                <p>- 프로 1단계 간소화</p>
                <p>- 1월 13일부터 기존 플래버의 레벨도 새로운 시스템에 맞춰 변경돼요. (예: 아마추어3 -> 아마추어5)</p>
                <p>- 자세한 스토리는 <a href='https://www.plabfootball.com/magazine/67/' target='_blank' rel='noopener noreferrer'>플랩 매거진</a>에서 확인하실 수 있어요.</p>
            </div>
        </div>
      
      <div className={styles.levelList}>
        {levels.map((level) => (
          <div key={level.id} className={styles.levelCard}>
            <div className={styles.levelHeader}>
              {getLevelIconUrl(level.name, level.id) && (
                <img 
                  src={getLevelIconUrl(level.name, level.id)} 
                  alt={`${level.name} 아이콘`}
                  className={styles.levelIcon}
                />
              )}
              <h2 className={styles.levelName}>{level.name}</h2>
            </div>
            <p className={styles.description}>{level.description}</p>
            <div className={styles.detailContainer}>
              {formatDetail(level.detail)}
            </div>
            
            <div className={styles.feedbackSection}>
                <p>{level.name}에 대한 설명으로 적절한가요?</p>
              <div className={styles.feedbackButtons}>
                {feedbackOptions.map((option) => (
                  <button
                    key={option}
                    className={`${styles.feedbackButton} ${
                      userFeedbacks[level.id] === option ? styles.active : ''
                    }`}
                    onClick={() => handleFeedback(level.id, option)}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>
          </div>
        ))}
      
        <div className={styles.levelCard}>
          <h2 className={styles.levelName}>추가 의견</h2>
          <p className={styles.feedbackDescription}>
            레벨 시스템 및 가이드에 대한 의견을 남겨주세요. 소중한 의견을 참고하여 더 좋은 서비스를 만들 수 있도록 하겠습니다.
          </p>
          <div className={styles.feedbackForm}>
            <Textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="레벨 가이드에 대한 추가 의견을 자유롭게 작성해주세요."
              rows={4}
              disabled={feedbackSubmitted}
            />
            <Button
              onClick={handleFeedbackSubmit}
              width="fullWidth"
              size="large"
              disabled={!feedback.trim() || feedbackSubmitted}
            >
              의견 제출하기
            </Button>
          </div>
        </div>
      </div>
      
      {showToast && (
        <Toast 
          message={toastMessage} 
          onClose={() => setShowToast(false)} 
        />
      )}
    </div>
  );
}